// Shared with mju-aioe/packages/shared/src/schemas/projectRuns.ts — keep the two identical.
/**
 * Runs: the record behind the work timeline. A **run** is one stretch of one
 * agent's work in one session — from the moment it was set going (a person's
 * message, another agent's hand-off, an automation) to the moment it stopped
 * (finished, failed, cancelled). Each repository's workbench writes its own
 * (src/acp/runLedger.ts) and mirrors them to the project's home, so the whole
 * project's timeline is readable with no repository open and by everyone on
 * the project:
 *
 *   POST /projects/v1/:id/roots/:rootId/runs    { runs[] } (at most 500) → { accepted }   upsert by `id`; the server sets `rootId`
 *   GET  /projects/v1/:id/runs?from=&to=&rootId=&limit=   { runs[], people{}, truncated? }
 *                                                every run that overlaps [from, to) (ISO), oldest first; a run still going overlaps "now"
 *
 * A run carries the same keys as a usage record and a live session
 * (`sessionId`, `teamId`, `rootId`, the task's board-local `id`), so cost and
 * the live view join to it without a lookup; it sits beside the live
 * session, never replaces it — a session has many runs. Contributors and
 * above post; viewers read; anyone else gets 404. A home keeps runs for at
 * least 90 days.
 *
 * **What a run is for.** The links say what the work was against, so a
 * timeline can be cut by any of them ({@link runMatches}): the task, the
 * plan and its step, the team, and the roadmap items the task or plan
 * serves — a repository's rows and, through them or directly, the project's.
 *
 * Words for people: run, asked by a person, handed over by an agent,
 * automation, cancelled, run time, tokens used.
 */
import { z } from "zod";
export const PROJECT_RUNS_SCHEMA_VERSION = 1;
const id = z.string().min(1).max(200);
/** Who set it going: a person's message, another agent's hand-off, or an automation (a routine, a schedule, a recovery). */
export const RUN_TRIGGERS = ["person", "agent", "automation"];
export const runTriggerSchema = z.enum(RUN_TRIGGERS);
export const RUN_OUTCOMES = ["ok", "error", "cancelled"];
export const runOutcomeSchema = z.enum(RUN_OUTCOMES);
/** A roadmap item: a repository's row (`rootId`), or the project's own (`rootId: ""`, an `R-n`). */
export const roadmapRefSchema = z.object({ rootId: z.string().max(200).default(""), id });
export const workRunSchema = z.looseObject({
    /** Unique within the repository; stable across re-posts (a run is posted when it starts and again when it ends). */
    id,
    /** The repository. A server sets it from the route. */
    rootId: z.string().max(200).default(""),
    /** The repository's name in words (AIOE 0.34.0), put on every answer; never stored. */
    rootName: z.string().max(200).optional(),
    sessionId: id,
    /** The agent's name, as its team knows it. */
    agent: z.string().min(1).max(200),
    /** Its team's id (the board's id); `solo` for a specialist or a chat. */
    teamId: z.string().min(1).max(200),
    teamName: z.string().max(200).optional(),
    runtime: z.string().max(100).optional(),
    provider: z.string().max(100).optional(),
    model: z.string().max(200).optional(),
    startedAt: z.string().max(50),
    /** Absent while it is still going. */
    endedAt: z.string().max(50).optional(),
    trigger: runTriggerSchema,
    /** Who or what set it going: the person, the agent that handed over (`actor`), the routine. */
    by: z
        .object({
        principal: z.string().max(200).optional(),
        actor: z.string().max(200).optional(),
        routine: z.object({ id, name: z.string().max(200).optional() }).optional(),
    })
        .optional(),
    /** Absent while it is still going. */
    outcome: runOutcomeSchema.optional(),
    tokens: z.object({ input: z.number().int().nonnegative(), output: z.number().int().nonnegative() }).optional(),
    costUsd: z.number().nonnegative().optional(),
    toolCalls: z.number().int().nonnegative().optional(),
    /** The context-budget profile the member ran with (workbench D272 part 3): how much of the model's window the run fills. */
    contextProfile: z.enum(["lean", "standard", "full"]).optional(),
    /** How many times the run compacted its context, and whether it ran out of room (workbench D272 part 3). */
    compactions: z.number().int().nonnegative().optional(),
    overflowed: z.literal(true).optional(),
    /** What it was asked, in a line (credentials masked). */
    title: z.string().max(300).optional(),
    /** The task it worked on: its board, its board-local id and its stable uid. */
    task: z.object({ board: id, id, uid: id.optional(), title: z.string().max(300).optional() }).optional(),
    /** The plan and the step of it the task carries. */
    plan: z.object({ id, name: z.string().max(100).optional(), step: z.string().max(100).optional() }).optional(),
    /** The roadmap items the work serves. */
    roadmap: z.array(roadmapRefSchema).max(20).optional(),
    /** The project task its task carries a part of. */
    projectTask: z.object({ uid: id }).optional(),
    intentId: id.optional(),
    conversationId: id.optional(),
    /** A node's dispatch, when one started it. */
    dispatchId: id.optional(),
    /** It ran in its own sandbox on a sandboxed Linux node (OpenShell); absent when it did not. */
    sandboxed: z.boolean().optional(),
    /**
     * The machine it ran on: a person's desktop or a node, by its fleet id and display name. A home sets it from the
     * device token that posted the run and ignores what the body says (AIOE 0.63.0); absent when a person posted it
     * without a machine. Never used to authorise anything.
     */
    place: z.object({ kind: z.enum(["desktop", "node"]), deviceId: id, name: z.string().max(200) }).optional(),
});
export const workRunsPostSchema = z.object({ runs: z.array(workRunSchema).max(500) });
// ── Reading runs ───────────────────────────────────────────────────────
const ms = (iso) => (iso ? Date.parse(iso) : Number.NaN);
/** How long it ran, in ms: to its end, or to `now` while it is still going. Never negative. */
export function runDurationMs(run, now) {
    const start = ms(run.startedAt);
    const end = run.endedAt ? ms(run.endedAt) : now;
    return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, end - start) : 0;
}
/** Whether any of it falls in [from, to). A run still going reaches `now`. */
export function runOverlaps(run, from, to, now) {
    const start = ms(run.startedAt);
    if (!Number.isFinite(start))
        return false;
    const end = run.endedAt ? ms(run.endedAt) : Math.max(now, start);
    return start < to && end >= from;
}
export function runMatches(run, f) {
    if (f.rootId !== undefined && run.rootId !== f.rootId)
        return false;
    if (f.agent !== undefined && run.agent !== f.agent)
        return false;
    if (f.teamId !== undefined && run.teamId !== f.teamId)
        return false;
    if (f.sessionId !== undefined && run.sessionId !== f.sessionId)
        return false;
    if (f.trigger !== undefined && run.trigger !== f.trigger)
        return false;
    if (f.task) {
        if (!run.task)
            return false;
        if (f.task.uid !== undefined) {
            if (run.task.uid !== f.task.uid)
                return false;
        }
        else if ((f.task.board !== undefined && run.task.board !== f.task.board) || (f.task.id !== undefined && run.task.id !== f.task.id))
            return false;
    }
    if (f.plan && (run.plan?.id !== f.plan.id || (f.plan.step !== undefined && run.plan.step !== f.plan.step)))
        return false;
    if (f.roadmap) {
        const want = f.roadmap;
        if (!(run.roadmap ?? []).some((r) => r.rootId === want.rootId && r.id === want.id))
            return false;
    }
    if (f.projectTask !== undefined && run.projectTask?.uid !== f.projectTask)
        return false;
    return true;
}
/** The numbers above a timeline. An agent is counted once per team it works in. */
export function runTotals(runs, now) {
    const agents = new Set();
    let runTimeMs = 0;
    let tokens = 0;
    let costUsd = 0;
    let cancelled = 0;
    let failed = 0;
    for (const r of runs) {
        agents.add(`${r.rootId}\u0000${r.teamId}\u0000${r.agent}`);
        runTimeMs += runDurationMs(r, now);
        tokens += (r.tokens?.input ?? 0) + (r.tokens?.output ?? 0);
        costUsd += r.costUsd ?? 0;
        if (r.outcome === "cancelled")
            cancelled++;
        if (r.outcome === "error")
            failed++;
    }
    return { runs: runs.length, agents: agents.size, runTimeMs, tokens, costUsd, cancelled, failed };
}
/** The same roadmap item, once. */
export function uniqueRoadmapRefs(refs) {
    const seen = new Set();
    return refs.filter((r) => {
        const key = `${r.rootId}\u0000${r.id}`;
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
}
export const RUN_TRIGGER_TEXT = { person: "Asked by a person", agent: "Handed over by an agent", automation: "Automation" };
export const RUN_OUTCOME_TEXT = { ok: "Finished", error: "Failed", cancelled: "Cancelled" };
//# sourceMappingURL=projectRuns.js.map